import React, { useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { isNil } from 'lodash';
import { Button, Row, Col } from 'antd';

import {
  AssignmentAdminDocumentModel,
  AssignmentAdminAdditionalInfoModel,
  AssignmentTypeEnum,
  AssignmentInfoAdminResponseModel,
} from 'api/adminApiClient';
import {
  MemberQuestionnaireSection,
  ClassificationQuestionnaireSection,
} from 'features/assignment/Questionnaire/QuestionnaireSections';
import BasePageCard from 'components/wrappers/BasePageCard';
import {
  AssignmentDetailsModel,
  getAssignmentPageIdLabel,
} from 'utils/assignmentUtils';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import BaseSectionMessage from 'components/wrappers/BaseSectionMessage';
import EditAssignmentAdditionalInformationModal from './EditAssignmentAdditionalInfoModal';
import DownloadAssignment from 'features/assignment/AssignmentDetailsPage/DownloadAssignment';
import AssignmentAdditionalInfoCard from 'features/assignment/AssignmentDetailsPage/AssignmentAdditionalInfoSection/AssignmentAdditionalInfoCard';

type Props = {
  onSubmit: () => void;
  assignmentPrimaryDetails: AssignmentInfoAdminResponseModel;
  assignmentAdditionalDetails?: AssignmentAdminDocumentModel;
  setPrimaryDetails: (data: AssignmentDetailsModel) => void;
};

export default function AdminAssignmentAdditionalInfoSection(props: Props) {
  const {
    assignmentPrimaryDetails,
    assignmentAdditionalDetails,
    setPrimaryDetails,
    onSubmit,
  } = props;
  const [isEditAdditionalInfoModalOpen, setIsEditAdditionalInfoModalOpen] =
    useState(false);
  const {
    assignmentId,
    assignmentKey,
    assignmentType,
    assignmentStatus,
    questionnaireInfo,
    renewStatus,
    creator,
    contractorQuestionnaireInfo,
    renewKind,
  } = assignmentPrimaryDetails;
  const { additionalInfo } =
    assignmentAdditionalDetails as AssignmentAdminDocumentModel;

  const isAdditionalInfo =
    !isNil(additionalInfo?.anticipatedWeeklyCommission) ||
    !isNil(additionalInfo?.grossMarginPerHour) ||
    !isNil(additionalInfo?.grossMarginPercent) ||
    !isNil(additionalInfo?.markup) ||
    !isNil(additionalInfo?.confirmationCommissionStructure);

  const pageLabel = getAssignmentPageIdLabel({
    assignmentStatus,
    renewStatus,
    renewKind,
  });

  return (
    <>
      {isEditAdditionalInfoModalOpen && (
        <EditAssignmentAdditionalInformationModal
          onClose={() => setIsEditAdditionalInfoModalOpen(false)}
          onSubmit={onSubmit}
          additionalInfo={additionalInfo as AssignmentAdminAdditionalInfoModel}
          assignmentId={assignmentId as string}
        />
      )}

      {pageLabel && <BasePageLabel label={pageLabel} creator={creator} />}

      <div className="page-box page-inner">
        <div className="page-inner-container">
          <div className="page-head">
            <div className="page-head__left">
              <div className="page-title page-head__title">{assignmentKey}</div>
            </div>
            <div className="page-head__actions">
              <div>
                <Button onClick={() => setIsEditAdditionalInfoModalOpen(true)}>
                  <EditOutlined />
                  Edit additional info
                </Button>
              </div>

              <DownloadAssignment
                assignmentId={assignmentId}
                assignmentStatus={assignmentStatus}
              />
            </div>
          </div>
          <Row gutter={[10, 10]}>
            <Col xs={24} md={12}>
              <BasePageCard
                title="Additional information"
                icons={<span className="icon icon-additional"></span>}
              >
                <div className="content-wrapper">
                  {!isAdditionalInfo && (
                    <BaseSectionMessage
                      title=""
                      message="Please fill in the assignment additional information."
                      type="info"
                    />
                  )}

                  {isAdditionalInfo && (
                    <AssignmentAdditionalInfoCard
                      additionalInfo={
                        additionalInfo as AssignmentAdminAdditionalInfoModel
                      }
                    />
                  )}
                </div>
              </BasePageCard>
            </Col>

            {assignmentType === AssignmentTypeEnum.ICAssignment && (
              <>
                {questionnaireInfo && (
                  <MemberQuestionnaireSection
                    assignmentId={assignmentId as string}
                    assignmentKey={assignmentKey}
                    setPrimaryDetails={setPrimaryDetails}
                    questionnaireInfo={questionnaireInfo}
                  />
                )}

                {contractorQuestionnaireInfo && (
                  <ClassificationQuestionnaireSection
                    assignmentId={assignmentId as string}
                    assignmentKey={assignmentKey}
                    setPrimaryDetails={setPrimaryDetails}
                    questionnaireInfo={contractorQuestionnaireInfo}
                  />
                )}
              </>
            )}
          </Row>
        </div>
      </div>
    </>
  );
}
